import React, { useEffect, useMemo, useState } from 'react';

import { Skeleton, Tooltip } from '@mui/material';
import styled from 'styled-components';

import { DeliveryIcon } from 'assets/icons/delivery-icon';
import { FailedDeliveryIcon } from 'assets/icons/failed-delivery-icon';
import { PersonIcon } from 'assets/icons/person-icon';
import { UnsubIcon } from 'assets/icons/unsub-icon';
import { InfoBanner } from 'src/app/components/banner/info';
import { FormContainer } from 'src/app/components/lib/form/form';
import { FunnelItems } from 'src/app/components/lib/funnel-item/funnel';
import { PageLoader } from 'src/app/components/lib/loader';
import { MenuItem } from 'src/app/components/lib/menu-item';
import { Select } from 'src/app/components/lib/select';
import { useGetAutomatedCampaignsPerformanceStatsQuery } from 'src/app/queries/marketing/get-automated-campaign-performance';
import { formatMoney } from 'src/app/utils/formatters';

import { AutomatedCampaignStatus } from '../../marketing-campaign-builder/builder-context';
import { emptyAccumulatorByChannel } from '../../utils/consts';
import {
  automatedDateDropdownOptions,
  CHANNEL_TYPES,
  channelDropdownOptions,
  DATE_TYPES,
  REVENUE_TYPES,
  revenueDropdownOptions,
} from '../../utils/types';
import { filteredAutomatedCampaignDataByChannel, getFunnelItems } from '../../utils/utils';

import type { SavedMarketingCampaign } from 'src/app/queries/marketing/get-campaigns';

export type AutomatedCampaignPerformanceOverviewProps = {
  campaignDetailsData: SavedMarketingCampaign;
  loading: boolean;
};

export default function AutomatedCampaignPerformanceOverview({
  campaignDetailsData,
  loading,
}: AutomatedCampaignPerformanceOverviewProps) {
  // State
  const [revenueDropdownOption, setRevenueDropdownOption] = useState<REVENUE_TYPES>(REVENUE_TYPES.ALL_REVENUE);
  const [dateDropdownOption, setDateDropdownOption] = useState<number>(DATE_TYPES.ONE_DAY);
  const [channelDropdownOption, setChannelDropdownOption] = useState<CHANNEL_TYPES>(CHANNEL_TYPES.EMAIL);

  useEffect(() => {
    if (!campaignDetailsData) {
      return;
    }

    const numChannelsActive =
      (campaignDetailsData.EmailConfig?.IsActive ? 1 : 0) +
      (campaignDetailsData.SMSConfig?.IsActive ? 1 : 0) +
      (campaignDetailsData.PushConfig?.IsActive ? 1 : 0);

    if (numChannelsActive > 1) {
      setChannelDropdownOption(CHANNEL_TYPES.ALL);
    } else if (campaignDetailsData.EmailConfig?.IsActive) {
      setChannelDropdownOption(CHANNEL_TYPES.EMAIL);
    } else if (campaignDetailsData.SMSConfig?.IsActive) {
      setChannelDropdownOption(CHANNEL_TYPES.SMS);
    } else if (campaignDetailsData.PushConfig?.IsActive) {
      setChannelDropdownOption(CHANNEL_TYPES.PUSH);
    }
  }, [campaignDetailsData]);

  // Queries
  const { data: campaignData } = useGetAutomatedCampaignsPerformanceStatsQuery({
    MarketingCampaignId: campaignDetailsData?.MarketingCampaignId ?? 0,
  });

  const filteredCampaignsbyMonth = filteredAutomatedCampaignDataByChannel(campaignData || [], channelDropdownOption);

  // Constants
  const deliveries = filteredCampaignsbyMonth[dateDropdownOption].Delivered;
  const recipients = filteredCampaignsbyMonth[dateDropdownOption].Recipients;
  const deliveryRate = Math.floor(((deliveries ?? 0) / (recipients ?? 1)) * 100);

  const funnelItems = useMemo(
    () =>
      getFunnelItems(
        filteredCampaignsbyMonth[dateDropdownOption] ?? emptyAccumulatorByChannel,
        channelDropdownOption,
        revenueDropdownOption
      ),
    [filteredCampaignsbyMonth, dateDropdownOption, revenueDropdownOption, channelDropdownOption]
  );

  return (
    <CampaignPage>
      {campaignDetailsData?.CampaignStatusId === AutomatedCampaignStatus.INACTIVE && (
        <InfoBanner copy='This campaign is inactive.' />
      )}
      <CardContainer>
        <StyledFormContainer>
          <StyledSelect
            automationId='campaign-automated-attribution-revenue-dropdown'
            id='campaign-automated-attribution-revenue-dropdown'
            value={revenueDropdownOption}
            onChange={({ target }) => setRevenueDropdownOption(target.value)}
          >
            {revenueDropdownOptions.map((option: { id: string; label: string }) => (
              <MenuItem
                automationId={`campaign-attribution-revenue-dropdown-${option?.id}`}
                key={option.id}
                value={option.id}
              >
                {option.label}
              </MenuItem>
            ))}
          </StyledSelect>
          <StyledSelect
            automationId='date-campaign-automated-attribution-revenue-dropdown'
            id='date-campaign-automated-attribution-revenue-dropdown'
            value={dateDropdownOption}
            onChange={({ target }) => setDateDropdownOption(target.value)}
          >
            {automatedDateDropdownOptions.map((option: { id: string; label: string; value: number }) => (
              <MenuItem
                automationId={`date-campaign-automated-attribution-revenue-dropdown-${option.id}`}
                key={option.value}
                value={option.value}
              >
                {option.label}
              </MenuItem>
            ))}
          </StyledSelect>
          <StyledSelect
            automationId='channel-campaign-attribution-channel-dropdown'
            id='channel-campaign-attribution-channel-dropdown'
            value={channelDropdownOption}
            onChange={({ target }) => setChannelDropdownOption(target.value)}
          >
            {channelDropdownOptions.map((option: { id: string; label: string; value: CHANNEL_TYPES }) => (
              <MenuItem
                automationId={`channel-campaign-attribution-channel-dropdown-${option.id}`}
                key={option.value}
                value={option.value}
              >
                {option.label}
              </MenuItem>
            ))}
          </StyledSelect>
          <StyledSpan>Attribution window is 7 days since message was sent</StyledSpan>
        </StyledFormContainer>
        <CardRow>
          <CardCol>
            <StatCard
              descriptor='Total revenue'
              icon={<PersonIcon />}
              loading={loading}
              stat={formatMoney(filteredCampaignsbyMonth[dateDropdownOption][revenueDropdownOption]?.TotalRevenue ?? 0)}
              tooltip='The total revenue generated from the campaign'
            />
            {channelDropdownOption !== CHANNEL_TYPES.PUSH && (
              <StatCard
                descriptor='Delivery rate'
                icon={<DeliveryIcon />}
                loading={loading}
                stat={deliveryRate ? `${deliveryRate}%` : '--'}
                tooltip='The percentage of messages that were successfully delivered'
              />
            )}
          </CardCol>
          <CardCol>
            <StatCard
              descriptor='Recipients'
              icon={<PersonIcon />}
              loading={loading}
              stat={filteredCampaignsbyMonth?.[dateDropdownOption]?.Recipients}
              tooltip='The number of messages sent'
            />
            {channelDropdownOption !== CHANNEL_TYPES.PUSH && (
              <StatCard
                descriptor='Failed Deliveries'
                icon={<FailedDeliveryIcon />}
                loading={loading}
                stat={filteredCampaignsbyMonth?.[dateDropdownOption]?.FailedDeliveries}
                tooltip='The number of messages that failed to be delivered (e.g. bounces, spam filters, etc)'
              />
            )}
          </CardCol>
          <CardCol>
            {channelDropdownOption !== CHANNEL_TYPES.PUSH && (
              <StatCard
                descriptor='Unsubscribes'
                icon={<UnsubIcon />}
                loading={loading}
                stat={filteredCampaignsbyMonth?.[dateDropdownOption]?.Unsubscribes}
                tooltip='The number of customers who unsubscribed from further communications after receiving their message'
              />
            )}
          </CardCol>
        </CardRow>
        <CardRow>
          <CampaignFunnelCard>
            {loading && <PageLoader />}

            {!loading && (
              <>
                <FunnelHeader>Campaign funnel</FunnelHeader>
                <CardRow>
                  <FunnelItems items={funnelItems} />
                </CardRow>
              </>
            )}
          </CampaignFunnelCard>
        </CardRow>
      </CardContainer>
    </CampaignPage>
  );
}

function StatCard({ descriptor, stat, icon, loading = false, tooltip }) {
  return (
    <Card>
      {loading && <Skeleton animation='wave' data-testid='skeleton' width='100%' />}
      {!loading && (
        <>
          <div>
            <CardStatText>{stat || '--'}</CardStatText>
            <Tooltip arrow placement='right' title={tooltip}>
              <CardDescriptorText>{descriptor}</CardDescriptorText>
            </Tooltip>
          </div>
          {icon}
        </>
      )}
    </Card>
  );
}

const StyledFormContainer = styled(FormContainer)`
  width: 100%;
  display: block;
  margin-left: var(--sizes-40);
`;

const StyledSelect = styled(Select)`
  width: auto;
  display: inline-flex;
  margin: 0 var(--sizes-40) var(--sizes-40) 0;

  .MuiInputBase-input {
    padding-right: 45px !important;
  }
`;

const FunnelHeader = styled.div`
  padding: var(--sizes-40);
  width: 100%;
  text-align: left;
  font: var(--font-headings-heading-3);
`;

const CampaignPage = styled.div``;

const CardRow = styled.div`
  display: flex;
  width: 100%;
`;

const CardContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const CardCol = styled.div`
  display: flex;
  flex-direction: column;
`;

const CardStatText = styled.div`
  font: var(--font-headings-heading-2);
  margin-bottom: var(--sizes-20);
`;

const CardDescriptorText = styled.span`
  font: var(--font-extra-small-12pt-normal);
  width: fit-content;
  display: block;
  border-bottom: 1px dashed var(--color-gray-90);
`;

const Card = styled.div`
  border-radius: 10px;
  border-width: 1px;
  border-style: solid;
  border-color: var(--color-gray-20);
  background-color: var(--color-brand-primary-white);
  padding: var(--sizes-70);
  margin: var(--sizes-40);
  display: flex;
  width: 355px;
  min-height: 115px;
  justify-content: space-between;
  align-items: center;
`;

const CampaignFunnelCard = styled.div`
  border-radius: 10px;
  border-width: 1px;
  border-style: solid;
  border-color: var(--color-gray-20);
  background-color: var(--color-brand-primary-white);
  margin: var(--sizes-40);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const StyledSpan = styled.span`
  font: var(--font-small-13pt-normal);
  line-height: 50px;
`;
